import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from '@/lib/i18n';
import { useState, useEffect, useRef, Suspense, type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { recentDonationsOptions, currentWindowKey } from '@/lib/queries';
import { AnimatedTotalRaised } from './-components/animated-total-raised';
import { SpanishIcon } from '@/routes/$portalId/-components/assets/languages/spanish-icon';
import { EnglishIcon } from '@/routes/$portalId/-components/assets/languages/english-icon';
import { FrenchIcon } from '@/routes/$portalId/-components/assets/languages/french-icon';
import { DeutchIcon } from '@/routes/$portalId/-components/assets/languages/deutch-icon';
import { NederlandsIcon } from '@/routes/$portalId/-components/assets/languages/nederlands-icon';
import { PortugueseIcon } from '@/routes/$portalId/-components/assets/languages/portuguese-icon';
import { ItalianIcon } from '@/routes/$portalId/-components/assets/languages/italian-icon';
import { CatalanIcon } from '@/routes/$portalId/-components/assets/languages/catalan-icon';
import { Layout } from './-components/layout';
import { LogoIcon } from './-components/logo-icon';
import { motion, AnimatePresence } from 'motion/react';

export const Route = createFileRoute('/www/live-counter')({
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex, nofollow' }],
	}),
	component: Component,
});

// ---------------------------------------------------------------------------
// Country flags (SVG icon components)
// ---------------------------------------------------------------------------

const FLAG_SIZE = 14;

const COUNTRY_FLAGS: Record<string, ReactNode> = {
	ES: <SpanishIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	GB: <EnglishIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	FR: <FrenchIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	DE: <DeutchIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	NL: <NederlandsIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	PT: <PortugueseIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	IT: <ItalianIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	AT: <DeutchIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
	AD: <CatalanIcon width={FLAG_SIZE} height={FLAG_SIZE} />,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ApiDonation = {
	amountCents: number;
	timestamp: number;
	campaignName: string | null;
	merchantName: string | null;
	city: string | null;
	country: string | null;
};

type FeedDonation = ApiDonation & { id: string };

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function Component() {
	// Track the current 5-minute window key (same as landing page)
	const [windowKey, setWindowKey] = useState(currentWindowKey);
	useEffect(() => {
		const timer = setInterval(() => {
			const key = currentWindowKey();
			if (key !== windowKey) setWindowKey(key);
		}, 10_000);
		return () => clearInterval(timer);
	}, [windowKey]);

	return (
		<Layout className="flex min-h-dvh flex-col items-center justify-center">
			<div className="flex w-full flex-col items-center px-6">
				<Suspense fallback={<CounterFallback />}>
					<LiveCounter windowKey={windowKey} />
				</Suspense>
			</div>
		</Layout>
	);
}

function CounterFallback() {
	return (
		<div className="text-primary text-center text-5xl font-bold tracking-tight md:text-7xl">
			<span className="opacity-40">...</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Live counter with donation feed
// Same mechanism as the landing page (AnimatedTotalRaised + real timestamps)
// ---------------------------------------------------------------------------

function LiveCounter({ windowKey }: { windowKey: string }) {
	const { language } = useTranslation();
	const { data } = useSuspenseQuery(recentDonationsOptions(windowKey));

	// Track arrived donations for the feed (using real timestamps, same as counter)
	const arrivedIdsRef = useRef(new Set<string>());
	const [feedItems, setFeedItems] = useState<FeedDonation[]>([]);

	// Reset feed on window change
	useEffect(() => {
		arrivedIdsRef.current = new Set<string>();
		setFeedItems([]);
	}, [windowKey]);

	// Poll for newly arrived donations at their real timestamps
	useEffect(() => {
		const tick = () => {
			const now = Date.now();
			let hasNew = false;
			const newArrivals: FeedDonation[] = [];
			for (let i = 0; i < data.donations.length; i++) {
				const d = data.donations[i]!;
				const id = `${windowKey}-${i}`;
				if (d.timestamp <= now && !arrivedIdsRef.current.has(id)) {
					arrivedIdsRef.current.add(id);
					newArrivals.push({ ...d, id });
					hasNew = true;
				}
			}
			if (hasNew) {
				setFeedItems((prev) => [...prev, ...newArrivals].slice(-20));
			}
		};

		tick();
		const timer = setInterval(tick, 50);
		return () => clearInterval(timer);
	}, [data, windowKey]);

	return (
		<div className="relative w-full">
			{/* Header — logo + tagline */}
			<div className="mb-4 flex flex-col items-center gap-3">
				<LogoIcon className="h-[50px] w-auto" style={{ '--color-primary': 'var(--primary)' } as React.CSSProperties} />
				<p className="text-sm tracking-wide">Donations collected in real time</p>
			</div>

			{/* Counter — centered, stable position */}
			<div className="text-primary text-center text-5xl font-bold tracking-tight md:text-7xl">
				<AnimatedTotalRaised
					totalAtWindowStartCents={data.totalAtWindowStartCents}
					donations={data.donations}
					language={language}
				/>
			</div>

			{/* Feed — absolutely positioned below the counter so it never shifts it */}
			<div className="absolute inset-x-0 top-full flex justify-center px-6">
				<DonationFeed donations={feedItems} />
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "EROSKI68 - Ayuda a víctimas - 03/2020" → "Ayuda a víctimas" */
function parseCampaignName(name: string | null): string | null {
	if (!name) return null;
	const parts = name.split(' - ');
	if (parts.length <= 1) return name.trim();
	// If the last segment looks like a date (e.g. "03/2020", "12/2026"), use the previous one
	const last = parts.at(-1)!.trim();
	if (/^\d{1,2}\/\d{4}$/.test(last) && parts.length >= 3) {
		return parts.at(-2)!.trim();
	}
	return last;
}

// ---------------------------------------------------------------------------
// Donation feed — motion layout animations
// ---------------------------------------------------------------------------

const MAX_VISIBLE = 12;

const sharedSpring = {
	type: 'spring' as const,
	stiffness: 320,
	damping: 30,
};

const enterTransition = {
	layout: sharedSpring,
	y: sharedSpring,
	opacity: { delay: 0.1, duration: 0.3, ease: 'easeOut' as const },
};

function DonationFeed({ donations }: { donations: FeedDonation[] }) {
	// Newest-first for display
	// oxlint-disable-next-line unicorn/no-array-reverse
	const allItems = [...donations].reverse();

	return (
		<div className="mt-4 w-full">
			{/* Waiting state */}
			<AnimatePresence>
				{allItems.length === 0 && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						className="flex items-center justify-center pt-4 text-xs text-gray-300"
					>
						<span className="flex items-center gap-2">
							<span className="relative flex h-1.5 w-1.5">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gray-300 opacity-75" />
								<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gray-300" />
							</span>
							Listening for donations...
						</span>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Donation rows */}
			<div className="flex flex-col items-center">
				<AnimatePresence initial={false}>
					{allItems.map((d, i) => (
						<DonationRow key={d.id} donation={d} index={i} />
					))}
				</AnimatePresence>
			</div>
		</div>
	);
}

function DonationRow({ donation, index }: { donation: FeedDonation; index: number }) {
	const flagIcon = donation.country ? (COUNTRY_FLAGS[donation.country] ?? null) : null;
	const amount = (donation.amountCents / 100).toFixed(2);
	const campaignLabel = parseCampaignName(donation.campaignName);
	const hasLocation = flagIcon || donation.city;

	const targetOpacity = index >= MAX_VISIBLE ? 0 : index === 0 ? 1 : Math.max(0.25, 1 - index * 0.1);

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: targetOpacity, y: 0 }}
			transition={enterTransition}
			className="w-full"
		>
			<div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 px-1 py-1.5">
				<span className="text-primary shrink-0 text-sm font-semibold tabular-nums">+{amount}€</span>
				{campaignLabel && (
					<>
						<span className="shrink-0 text-sm text-gray-400">para</span>
						<span className="text-sm font-medium text-gray-700">{campaignLabel}</span>
					</>
				)}
				{donation.merchantName && (
					<span className="text-sm text-gray-400">
						en {donation.merchantName}
						{hasLocation && ', '}
					</span>
				)}
				{hasLocation && (
					<span className="flex items-center gap-1 text-sm text-gray-400">
						{donation.city}
						{flagIcon}
					</span>
				)}
			</div>
		</motion.div>
	);
}
